import { useState, useRef, useEffect } from "react";

const API_URL = process.env.REACT_APP_WAVMARK_API || "https://tribal-wavmark-api.onrender.com";

export default function FingerprintFinder() {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState("file");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const reset = () => { setResult(null); setError(null); setFile(null); setUrl(""); };

  const run = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      let res;
      if (mode === "file") {
        const form = new FormData();
        form.append("file", file);
        form.append("run_watermark_check", "true");
        res = await fetch(`${API_URL}/fingerprint`, { method: "POST", body: form });
      } else {
        res = await fetch(`${API_URL}/fingerprint/url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url, run_watermark_check: true }),
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Request failed");
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const C = {
    bg: "#06060a", card: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.08)",
    text: "#f0ede8", muted: "rgba(240,237,232,0.5)", purple: "#7B5EA7", teal: "#0D9488",
    orange: "#E85D24",
  };

  const matchColor = (type) => ({ visual: C.purple, audio: C.teal, both: C.orange, watermark: "#22c55e" }[type] || C.muted);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: C.bg, minHeight: "100vh", color: C.text, padding: "40px 24px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .drop-zone { border: 1.5px dashed rgba(123,94,167,0.4); border-radius: 14px; padding: 48px 24px; text-align: center; cursor: pointer; transition: all 0.2s; background: rgba(123,94,167,0.03); }
        .drop-zone:hover { border-color: rgba(123,94,167,0.7); background: rgba(123,94,167,0.06); }
        .btn { border: none; border-radius: 8px; padding: 12px 28px; font-size: 14px; font-weight: 500; cursor: pointer; font-family: inherit; transition: all 0.2s; }
        .btn-primary { background: #7B5EA7; color: #fff; } .btn-primary:hover { background: #6a4f93; }
        .btn-primary:disabled { background: rgba(123,94,167,0.4); cursor: not-allowed; }
        .btn-ghost { background: transparent; color: rgba(240,237,232,0.6); border: 0.5px solid rgba(255,255,255,0.15); }
        .btn-ghost:hover { border-color: rgba(255,255,255,0.35); color: #f0ede8; }
        .tab { padding: 8px 18px; border-radius: 6px; font-size: 13px; cursor: pointer; border: none; font-family: inherit; transition: all 0.2s; }
        .tab.active { background: rgba(123,94,167,0.15); color: #a78fd4; }
        .tab.inactive { background: transparent; color: rgba(240,237,232,0.4); }
        .match-card { background: rgba(255,255,255,0.03); border: 0.5px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 16px 20px; margin-bottom: 10px; }
        .badge { display: inline-block; font-size: 11px; font-weight: 500; padding: 3px 10px; border-radius: 20px; letter-spacing: 0.04em; }
        input[type=text], input[type=url] { background: rgba(255,255,255,0.04); border: 0.5px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 12px 16px; font-size: 14px; color: #f0ede8; font-family: inherit; width: 100%; outline: none; transition: border-color 0.2s; }
        input:focus { border-color: rgba(123,94,167,0.5); }
      `}</style>

      <div style={{ maxWidth: 680, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: C.purple, letterSpacing: "0.08em", marginBottom: 10 }}>TRIBAL TECH</div>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 36, fontWeight: 400, marginBottom: 8 }}>Fingerprint Finder</h1>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7 }}>Upload a suspected leak or paste a URL. We'll match it against your entire catalog using visual and audio fingerprinting.</p>
        </div>

        {/* Catalog stats */}
        <CatalogStats apiUrl={API_URL} C={C} />

        {/* Mode tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24, background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: 4, width: "fit-content" }}>
          <button className={`tab ${mode === "file" ? "active" : "inactive"}`} onClick={() => { setMode("file"); reset(); }}>Upload file</button>
          <button className={`tab ${mode === "url" ? "active" : "inactive"}`} onClick={() => { setMode("url"); reset(); }}>Check URL</button>
        </div>

        {/* Input */}
        <div style={{ background: C.card, border: `0.5px solid ${C.border}`, borderRadius: 16, padding: 28, marginBottom: 24 }}>
          {mode === "file" ? (
            <>
              <div className="drop-zone" onClick={() => fileRef.current.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); setResult(null); }}>
                <input ref={fileRef} type="file" accept="video/*,audio/*" style={{ display: "none" }}
                  onChange={e => { setFile(e.target.files[0]); setResult(null); }} />
                {file ? (
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>{file.name}</div>
                    <div style={{ fontSize: 13, color: C.muted }}>{(file.size / (1024*1024)).toFixed(1)} MB</div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 28, marginBottom: 12, color: C.purple }}>◈</div>
                    <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Drop a video or audio file</div>
                    <div style={{ fontSize: 13, color: C.muted }}>MP4, MOV, MKV, MP3, WAV — up to 500MB</div>
                  </div>
                )}
              </div>
              {file && <button className="btn btn-ghost" style={{ marginTop: 12, fontSize: 12, padding: "6px 14px" }} onClick={reset}>Clear</button>}
            </>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>Paste a URL from a piracy site or suspected leak</div>
              <input type="url" placeholder="https://example.com/video.mp4" value={url} onChange={e => setUrl(e.target.value)} />
              <UrlCoverageNote C={C} />
            </div>
          )}
        </div>

        {/* Run button */}
        <button className="btn btn-primary" style={{ width: "100%", padding: "14px", fontSize: 15, marginBottom: 32 }}
          disabled={loading || (mode === "file" ? !file : !url)} onClick={run}>
          {loading ? "Analyzing..." : "Find matches"}
        </button>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(232,93,36,0.08)", border: "0.5px solid rgba(232,93,36,0.3)", borderRadius: 12, padding: "16px 20px", marginBottom: 24, fontSize: 14, color: "#f0a080" }}>
            {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div>
            {/* Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
              {[
                { label: "Matches found", val: result.total_matches, color: result.total_matches > 0 ? "#22c55e" : C.muted },
                { label: "Catalog size", val: `${result.catalog_size} videos`, color: C.text },
                { label: "Scan time", val: `${result.processing_time_seconds}s`, color: C.text },
              ].map((s, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: `0.5px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 500, color: s.color, marginBottom: 4 }}>{s.val}</div>
                  <div style={{ fontSize: 11, color: C.muted, letterSpacing: "0.04em" }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Watermark */}
            {result.watermark_detected && (
              <div style={{ background: "rgba(34,197,94,0.06)", border: "0.5px solid rgba(34,197,94,0.3)", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontSize: 18, color: "#22c55e" }}>◉</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "#22c55e", marginBottom: 2 }}>Watermark detected</div>
                  <div style={{ fontSize: 13, color: "rgba(34,197,94,0.7)" }}>Cohort: {result.watermark_cohort}</div>
                </div>
              </div>
            )}

            {/* Fingerprints */}
            <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
              <span className="badge" style={{ background: result.phash ? "rgba(123,94,167,0.15)" : "rgba(255,255,255,0.05)", color: result.phash ? "#a78fd4" : C.muted }}>
                Visual hash: {result.phash ? "extracted" : "none"}
              </span>
              <span className="badge" style={{ background: result.audio_fingerprint ? "rgba(13,148,136,0.15)" : "rgba(255,255,255,0.05)", color: result.audio_fingerprint ? "#4db6ac" : C.muted }}>
                Audio hash: {result.audio_fingerprint ? "extracted" : "none"}
              </span>
            </div>

            {/* Match list */}
            {result.total_matches > 0 ? (
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: C.muted, letterSpacing: "0.08em", marginBottom: 14 }}>MATCHES</div>
                {result.matches.map((m, i) => (
                  <div key={i} className="match-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{m.file_name}</div>
                      <div style={{ fontSize: 18, fontWeight: 500, color: matchColor(m.match_type) }}>{Math.round(m.confidence * 100)}%</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <span className="badge" style={{ background: `${matchColor(m.match_type)}18`, color: matchColor(m.match_type) }}>{m.match_type}</span>
                      {m.drive && <span className="badge" style={{ background: "rgba(255,255,255,0.05)", color: C.muted }}>{m.drive}</span>}
                      {m.watermark_cohort && <span className="badge" style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>{m.watermark_cohort}</span>}
                      {m.duration_seconds && <span className="badge" style={{ background: "rgba(255,255,255,0.05)", color: C.muted }}>{Math.round(m.duration_seconds)}s</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: C.muted }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>◌</div>
                <div style={{ fontSize: 15, marginBottom: 6 }}>No matches found</div>
                <div style={{ fontSize: 13 }}>This content doesn't match anything in your catalog</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Honest coverage note for the URL tab. yt-dlp handles major
// tube sites end-to-end (site-specific extractors ship with the
// package), but has no extractor for leak aggregators or file
// hosts — those URLs are an HTML viewer page with the real video
// behind JavaScript, and yt-dlp's generic fallback can't find it.
// Dismissable via localStorage so power users aren't nagged;
// visible by default so first-time users don't paste a Bunkr
// link and get a confusing "could not download" error.
function UrlCoverageNote({ C }) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('fp_url_coverage_dismissed') === '1'
  );
  if (dismissed) return null;
  const dismiss = () => {
    localStorage.setItem('fp_url_coverage_dismissed', '1');
    setDismissed(true);
  };
  return (
    <div style={{
      marginTop: 12,
      background: 'rgba(123,94,167,0.06)',
      border: '0.5px solid rgba(123,94,167,0.25)',
      borderRadius: 10,
      padding: '12px 14px',
      fontSize: 12,
      color: 'rgba(240,237,232,0.72)',
      lineHeight: 1.55,
      position: 'relative',
    }}>
      <button onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute', top: 8, right: 10,
          background: 'transparent', border: 'none', color: C.muted,
          cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1,
        }}>×</button>
      <div style={{ paddingRight: 20 }}>
        <span style={{ color: '#a78fd4', fontWeight: 500 }}>Currently supported:</span>{' '}
        major tube sites (Pornhub, xHamster, Spankbang, XVideos, RedTube, YouPorn, TNAFlix, Eporner, and similar).
        <br/>
        <span style={{ color: '#a78fd4', fontWeight: 500 }}>Not yet supported via URL:</span>{' '}
        leak aggregators and file hosts (Thothub, Coomer, Bunkr, Cyberdrop, Fapello, Leakedzone).
        For those, please use the <b>Upload file</b> tab — download the file manually from the source and drop it here to run fingerprint matching.
      </div>
    </div>
  );
}

function CatalogStats({ apiUrl, C }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${apiUrl}/catalog/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, [apiUrl]);

  if (!stats) return null;
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: `0.5px solid rgba(255,255,255,0.07)`, borderRadius: 10, padding: "12px 20px", marginBottom: 28, display: "flex", gap: 24, fontSize: 13, color: C.muted }}>
      <span><b style={{ color: C.text, fontWeight: 500 }}>{stats.total_videos}</b> videos in catalog</span>
      <span><b style={{ color: C.text, fontWeight: 500 }}>{stats.coverage_percent}%</b> fingerprinted</span>
    </div>
  );
}

